import { ProductBuilderMachineContext } from '../types';
import { getDayColourOptions, productHasColour } from './colour';
import { productHasVariantWithMaterialType } from './material';
import { productPropertySummary } from './productPropertySummary';

type HoneycombLineItems = (
  context: ProductBuilderMachineContext,
) => {
  _ROOM: string;
  _MOUNTTYPE: string;
  _SIZETYPE: string;
  _BLINDTYPE: string;
  _SLATSIZE: string;
  _CLEAT: string;
  _COLORCODE: string;
  _COLORCODE2: string;
  _CONTROLTYPE: string;
  _HDBRACKET: string;
  _HEADRAILMAT: string;
  _HEADRAILCOLOR: string;
  _LIGHTBLOCKCOLORID: string;
  _MATCHITEM: string;
  _PGMCODE: string;
  _PGMCODE2: string;
  _PULLCORDPOS1: string;
  _POLELENGTH: string;
  _POLEQTY: string;
  _PULLCORDLENGTH: string;
  _REMARK: string;
  _STASSELTYPE: string;
  _STACKID: string;
  _WALLANCHOR: string;
  _dynamic_product: boolean;
};

export const getHoneycombLineItems: HoneycombLineItems = context => {
  const { custom } = context;
  const productSummaryCapture = productPropertySummary(context);
  let materialType, fabricSize, driveControl, railColour;
  materialType =
    // One Tone colours (WSP) - check first
    (custom.materialType && custom.materialType === 'Light Filter' && (custom.colour?.slice(8) === 'Chantilly Lace One Tone'
      || custom.colour?.slice(8) === 'White Sand One Tone'
      || custom.colour?.slice(8) === 'Cashmere One Tone'
      || custom.colour?.slice(8) === 'Calm Tone One Tone'
      || custom.colour?.slice(8) === 'Rustic Beige One'
      || custom.colour?.slice(8) === 'Vanilla Ice Cream One Tone'
      || custom.colour?.slice(8) === 'Horizon One Tone'
      || custom.colour?.slice(8) === 'Haze One Tone'))
      ? 'WSP'
      : (custom.materialTypeNight && custom.materialTypeNight === 'Light Filter' && (custom.colourNight?.slice(8) === 'Chantilly Lace One Tone'
        || custom.colourNight?.slice(8) === 'White Sand One Tone'
        || custom.colourNight?.slice(8) === 'Cashmere One Tone'
        || custom.colourNight?.slice(8) === 'Calm Tone One Tone'
        || custom.colourNight?.slice(8) === 'Rustic Beige One'
        || custom.colourNight?.slice(8) === 'Vanilla Ice Cream One Tone'
        || custom.colourNight?.slice(8) === 'Horizon One Tone'
        || custom.colourNight?.slice(8) === 'Haze One Tone'))
        ? 'WSP'
        // Storm Cloud, Winter Solstice & Premium (Designer Fabrics) (DF) - specific Blockout colours
        : (custom.materialType && custom.materialType === "Blockout"
          && (custom.colour?.slice(8)?.trim() === 'Winter Solstice'
          || custom.colour?.slice(8)?.trim() === 'Storm Cloud'
          || custom.colour?.slice(8)?.trim()?.startsWith('Premium')))
          ? 'DF'
          // Sheer (SH)
          : (custom.materialType && custom.materialType === 'Sheer')
            ? 'SH'
            : (custom.materialTypeNight && custom.materialTypeNight === 'Sheer')
              ? 'SH'
              // All other Blockout colours (RD)
              : (custom.materialType && custom.materialType === "Blockout")
                ? 'RD'
                // All other Light Filter colours (LF)
                : (custom.materialType && custom.materialType === 'Light Filter')
                  ? 'LF'
                  : (custom.materialTypeNight && custom.materialTypeNight === 'Light Filter')
                    ? 'LF'
                    // Default fallback
                    : 'DF';

  fabricSize =
    custom.cellDesign === 'Double Cell 45mm'
      ? 'CU03'
      : custom.cellDesign === 'Single Cell 45mm'
      ? 'CU01'
      : 'CU06';

  driveControl =
    custom.driveControl === 'Drive Control: Cordless'
      ? 'Cordless'
      : custom.driveControl === 'Drive Control: Corded'
      ? 'Corded'
      : custom.driveControl === 'Drive Control: Cordloop'
      ? 'Cordloop'
      : '';

  railColour =
    custom.railColour === 'White'
      ? '3058'
      : custom.railColour === 'Beige'
      ? '3579'
      : custom.railColour === 'Silver'
      ? '3129'
      : '3212';

  const lightBlockColorId =
    (custom.railColour === 'White' && (custom.colour?.slice(8) === 'Chantilly Lace One Tone'
    || custom.colour?.slice(8) === 'White Sand One Tone'
    || custom.colour?.slice(8) === 'Cashmere One Tone'
    || custom.colour?.slice(8) === 'Calm Tone One Tone'
    || custom.colour?.slice(8) === 'Rustic Beige One'
    || custom.colour?.slice(8) === 'Vanilla Ice Cream One Tone'
    || custom.colour?.slice(8) === 'Horizon One Tone'
    || custom.colour?.slice(8) === 'Haze One Tone'))
      ? '3058'
      : custom.railColour === 'White'
      ? '3058'
      : custom.railColour === 'Beige'
      ? '3094'
      : custom.railColour === 'Silver'
      ? '3129'
      : '3212';

  const currentVariant = context.productWithMeasurements
    ? context.productWithMeasurements.data.find(
        ({ attributes: { freight } }) => freight === context.custom.shipping,
      )
    : null;

  const baseProduct = context.products.filter(product =>
    product.variants.some(
      variant =>
        variant.id ===
        (currentVariant ? currentVariant.attributes.base_variant_id : null),
    ),
  );

  const colourOptions = baseProduct[0].type.includes('Day/Night') ||
    baseProduct[0].type.includes('Day Night')
    ? getDayColourOptions(
      context.products
        .filter(
          productHasVariantWithMaterialType(context.custom.materialType),
        )
        .filter(productHasColour(context.custom.colourNight)),
    )
    : '';

  let dayColour =
    colourOptions !== ''
      ? colourOptions.find(colour => colour.name === context.custom.dayColour)
      : null;

  let dayColourCodeArray =
    colourOptions && dayColour ? dayColour['name'].match(/\(([^)]+)\)/) : '';

  const dayColourCode =
    dayColourCodeArray !== '' && dayColourCodeArray !== null
      ? dayColourCodeArray[1].trim()
      : '';

  const colourCode = baseProduct
    ? baseProduct[0].tags
        .find(tag => tag.includes('Colour Code'))
        ?.replace('Colour Code: ', '')
    : '';

  const stackSide =
    typeof custom.stackSide !== 'undefined' ? custom.stackSide : '';

  const controlType =
    baseProduct && baseProduct[0].type === 'Honeycomb Dual View Corded'
      ? 'Corded TDBU'
      : baseProduct && baseProduct[0].type === 'Honeycomb Dual View Cordless'
      ? 'Cordless TDBU'
      : baseProduct && baseProduct[0].type === 'Honeycomb Standard Corded'
      ? 'Corded'
      : baseProduct && baseProduct[0].type === 'Honeycomb Standard Cordless'
      ? 'Cordless'
      : baseProduct && baseProduct[0].type === 'Honeycomb Standard Cordloop'
      ? 'Cordloop'
      : baseProduct && baseProduct[0].type === 'Honeycomb Day/Night Corded'
      ? 'Corded Day&Night'
      : baseProduct && baseProduct[0].type === 'Honeycomb Day/Night Cordless'
      ? 'Cordless Day&Night'
      : baseProduct && baseProduct[0].type === 'Honeycomb Smartfit'
      ? 'Smartfit'
      : baseProduct && baseProduct[0].type === 'Honeycomb Skylight'
      ? 'Smartfit For Slope Window'
      : baseProduct && baseProduct[0].type === 'Honeycomb Vertical'
      ? 'Vertical'
      : baseProduct && baseProduct[0].type === 'Honeycomb SmartFit Day Night'
      ? 'SmartFit Day&Night'
      : '';

  let pullcord_controlType;
  if (
    controlType.includes('Cordless') ||
    controlType.includes('Day&Night') ||
    controlType.includes('TDBU')
  ) {
    pullcord_controlType = true;
    console.log('pullcord_controlType --> ', pullcord_controlType);
  }

  return {
    _ROOM: custom.roomName,
    _MOUNTTYPE: custom.fitting === 'Reveal Fit' ? 'IN' : 'OUT',
    _SIZETYPE: custom.fitting === 'Reveal Fit' ? 'WS' : 'MFF',
    _BLINDTYPE: 'single',
    _SLATSIZE: fabricSize,
    _CLEAT: driveControl === 'Corded' || driveControl === 'Cordloop' ? 'Y' : '',
    _COLORCODE:
      typeof custom.dayFabricPosition !== 'undefined' &&
      custom.dayFabricPosition === 'Top' &&
      dayColourCode
        ? dayColourCode
        : colourCode
        ? colourCode
        : '', // This would be the colour code for day
    _COLORCODE2:
      typeof custom.dayFabricPosition !== 'undefined' &&
      custom.dayFabricPosition === 'Bottom' &&
      dayColourCode
        ? dayColourCode
        : typeof custom.dayFabricPosition !== 'undefined' &&
          custom.dayFabricPosition === 'Top' &&
          colourCode
        ? colourCode
        : '', // This would be the colour code for night
    _CONTROLTYPE: controlType,
    _HDBRACKET: '', // Veneta only
    _HEADRAILMAT: 'AL',
    _HEADRAILCOLOR: railColour, // Colour of headrail - white 3058, silver 3129, Black 3212, beige 3579
    _LIGHTBLOCKCOLORID:
      typeof custom.lightGuards !== 'undefined' &&
      typeof custom.lightGuards === 'number'
        ? lightBlockColorId
        : '', // Light guard colour to match headrail colour
    _MATCHITEM: '', // Veneta only
    _PGMCODE:
      typeof custom.dayFabricPosition !== 'undefined' &&
      custom.dayFabricPosition === 'Top'
        ? 'SH'
        : materialType,
    _PGMCODE2:
      typeof custom.dayFabricPosition !== 'undefined' &&
      custom.dayFabricPosition === 'Bottom'
        ? 'SH'
        : typeof custom.dayFabricPosition !== 'undefined' &&
          custom.dayFabricPosition === 'Top'
        ? materialType
        : '', // Fabcric type code for secondary fabric in day/night blinds only
    _PULLCORDPOS1:
      custom.liftPosition === 'Right' && !pullcord_controlType
        ? 'R'
        : custom.liftPosition === 'Left' && !pullcord_controlType
        ? 'L'
        : '',
        _POLELENGTH:
        typeof custom.extensionPole !== 'undefined' &&
          typeof custom.extensionPole === 'number'
          ? custom.extensionPole === 38160223961261
            ? '1524'
            : custom.extensionPole === 44157374333101
              ? '915'
              : ''
          : '',
    _POLEQTY:
      typeof custom.extensionPole !== 'undefined' &&
      typeof custom.extensionPole === 'number'
        ? '1'
        : '',
    _PULLCORDLENGTH: '', // Veneta only
    _REMARK: '', // Always empty
    _STASSELTYPE: driveControl === 'Corded' ? 'ST01' : '',
    _STACKID:
      stackSide === 'Left'
        ? 'L'
        : stackSide === 'Right'
        ? 'R'
        : stackSide === 'Centre Opening'
        ? 'CO'
        : '', // Vertical blind stack direction
    _WALLANCHOR: 'N',
    _dynamic_product: true,
    ...productSummaryCapture,
  };
};
